import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import ApiService from "../../services/api";
import DeviceTokenSettings from "./DeviceTokenSettings";

interface UpdateProfileRequest {
  username: string;
  email: string;
  phone?: string;
  current_password?: string;
  new_password?: string;
}

const ProfileSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [countryCode, setCountryCode] = useState<"US" | "MX">("US");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        username: user.username,
        email: user.email,
        phone: user.phone || "",
      }));

      // Detect country code from existing phone
      if (user.phone) {
        const digitsOnly = user.phone.replace(/\D/g, "");
        if (digitsOnly.startsWith("52")) {
          setCountryCode("MX");
        } else {
          setCountryCode("US");
        }
      }
    }
  }, [user]);

  const validateEmail = (email: string): boolean => {
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }

    // Validar que sea un dominio permitido (Ionos)
    const allowedDomains = ["@pxgrouptexas.com", "@ionos.com", "@1and1.com"];

    return allowedDomains.some((domain) =>
      email.toLowerCase().endsWith(domain)
    );
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional

    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, "");

    // USA: +1 (10 digits) = 11 total
    // Mexico: +52 (10 digits) = 12 total
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      return true; // USA
    }
    if (digitsOnly.length === 12 && digitsOnly.startsWith("52")) {
      return true; // Mexico
    }

    return false;
  };

  const formatPhoneNumber = (value: string, country: "US" | "MX"): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");

    if (country === "US") {
      // USA format: +1 (XXX) XXX-XXXX
      // Remove country code if present
      const localNumber = digitsOnly.startsWith("1")
        ? digitsOnly.slice(1)
        : digitsOnly;

      if (localNumber.length === 0) return "";

      // Limit to exactly 10 digits for USA
      const limitedNumber = localNumber.slice(0, 10);

      const match = limitedNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
      if (match) {
        let formatted = "+1";
        if (match[1]) formatted += ` (${match[1]}`;
        if (match[2]) formatted += `) ${match[2]}`;
        if (match[3]) formatted += `-${match[3]}`;
        return formatted;
      }
    } else {
      // Mexico format: +52 XX XXXX XXXX
      // Remove country code if present
      const localNumber = digitsOnly.startsWith("52")
        ? digitsOnly.slice(2)
        : digitsOnly;

      if (localNumber.length === 0) return "";

      // Limit to exactly 10 digits for Mexico
      const limitedNumber = localNumber.slice(0, 10);

      const match = limitedNumber.match(/^(\d{0,2})(\d{0,4})(\d{0,4})$/);
      if (match) {
        let formatted = "+52";
        if (match[1]) formatted += ` ${match[1]}`;
        if (match[2]) formatted += ` ${match[2]}`;
        if (match[3]) formatted += ` ${match[3]}`;
        return formatted;
      }
    }

    return value;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validar email
    if (!validateEmail(formData.email)) {
      setMessage({
        type: "error",
        text: "Email must be from an authorized domain (@pxgrouptexas.com, @ionos.com, @1and1.com)",
      });
      return;
    }

    // Validar teléfono
    if (formData.phone && !validatePhone(formData.phone)) {
      setMessage({
        type: "error",
        text: "Phone number must be from USA (+1) or Mexico (+52). Format: +1 (555) 123-4567 or +52 55 1234 5678",
      });
      return;
    }

    // Validar contraseñas si se está cambiando
    if (showPasswordSection) {
      if (!formData.current_password) {
        setMessage({
          type: "error",
          text: "Current password is required to change password",
        });
        return;
      }

      if (formData.new_password !== formData.confirm_password) {
        setMessage({
          type: "error",
          text: "New passwords do not match",
        });
        return;
      }

      if (formData.new_password.length < 6) {
        setMessage({
          type: "error",
          text: "New password must be at least 6 characters",
        });
        return;
      }
    }

    setLoading(true);

    try {
      const updateData: UpdateProfileRequest = {
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
      };

      if (showPasswordSection && formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const response = await ApiService.patch<{ user: any }>(
        `/users/${user?.id}`,
        updateData
      );

      // Actualizar el contexto de autenticación
      if (response.user) {
        updateUser(response.user);
      }

      setMessage({
        type: "success",
        text: "Profile updated successfully",
      });

      // Limpiar campos de contraseña
      setFormData((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));
      setShowPasswordSection(false);
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.response?.data?.detail || "Failed to update profile",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow digits, +, spaces, parentheses, and hyphens
    const sanitized = value.replace(/[^0-9+\s()\-]/g, "");
    const formatted = formatPhoneNumber(sanitized, countryCode);
    setFormData({
      ...formData,
      phone: formatted,
    });
  };

  const handleCountryChange = (country: "US" | "MX") => {
    setCountryCode(country);
    // Reformat existing phone number with new country code
    if (formData.phone) {
      const digitsOnly = formData.phone.replace(/\D/g, "");
      // Remove old country code
      const localNumber =
        digitsOnly.startsWith("1") || digitsOnly.startsWith("52")
          ? digitsOnly.slice(digitsOnly.startsWith("52") ? 2 : 1)
          : digitsOnly;

      // Limit to 10 digits
      const limitedNumber = localNumber.slice(0, 10);

      const formatted = formatPhoneNumber(limitedNumber, country);
      setFormData({
        ...formData,
        phone: formatted,
      });
    }
  };

  return (
    <div className={`p-6 ${isDarkMode ? "bg-gray-900" : "bg-transparent"}`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p
            className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Manage your account settings and preferences
          </p>
        </div>

        {/* Message Alert */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === "success"
                ? isDarkMode
                  ? "bg-green-900/50 text-green-200 border border-green-800"
                  : "bg-green-50 text-green-800 border border-green-200"
                : isDarkMode
                ? "bg-red-900/50 text-red-200 border border-red-800"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Settings Panel */}
          <div className="lg:col-span-2">
            <div
              className={`rounded-lg shadow-md p-6 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <h2
                className={`text-xl font-semibold mb-6 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Account Information
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Username */}
                <div>
                  <label
                    htmlFor="username"
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-orange-500"
                    } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-orange-500"
                    } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                    required
                  />
                  <p
                    className={`mt-1 text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Must be from authorized domains: @pxgrouptexas.com,
                    @ionos.com, @1and1.com
                  </p>
                </div>

                {/* Phone Number */}
                <div>
                  <label
                    htmlFor="phone"
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Phone Number
                  </label>
                  <div className="flex gap-2">
                    {/* Country Selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          handleCountryChange(
                            countryCode === "US" ? "MX" : "US"
                          )
                        }
                        className={`h-full px-3 py-2 rounded-lg border flex items-center gap-2 transition-colors ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                            : "bg-white border-gray-300 hover:bg-gray-50"
                        }`}
                        title={
                          countryCode === "US" ? "United States" : "Mexico"
                        }
                      >
                        <span
                          className={`2xl ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {countryCode === "US" ? "🇺🇸" : "🇲🇽"}
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {countryCode === "US" ? "+1" : "+52"}
                        </span>
                      </button>
                    </div>

                    {/* Phone Input */}
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder={
                        countryCode === "US"
                          ? "+1 (555) 123-4567"
                          : "+52 55 1234 5678"
                      }
                      maxLength={20}
                      className={`flex-1 px-4 py-2 rounded-lg border ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500 placeholder-gray-500"
                          : "bg-white border-gray-300 text-gray-900 focus:border-orange-500 placeholder-gray-400"
                      } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                    />
                  </div>
                  <p
                    className={`mt-1 text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Click the flag to switch between USA and Mexico. Used for
                    two-factor authentication (2FA)
                  </p>
                </div>

                {/* Change Password Section */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowPasswordSection(!showPasswordSection)}
                    className={`flex items-center text-sm font-medium ${
                      isDarkMode
                        ? "text-orange-400 hover:text-orange-300"
                        : "text-orange-600 hover:text-orange-700"
                    }`}
                  >
                    <svg
                      className={`w-4 h-4 mr-2 transition-transform ${
                        showPasswordSection ? "rotate-90" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    Change Password
                  </button>

                  {showPasswordSection && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label
                          htmlFor="current_password"
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Current Password
                        </label>
                        <input
                          type="password"
                          id="current_password"
                          name="current_password"
                          value={formData.current_password}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 rounded-lg border ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500"
                              : "bg-white border-gray-300 text-gray-900 focus:border-orange-500"
                          } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="new_password"
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          New Password
                        </label>
                        <input
                          type="password"
                          id="new_password"
                          name="new_password"
                          value={formData.new_password}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 rounded-lg border ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500"
                              : "bg-white border-gray-300 text-gray-900 focus:border-orange-500"
                          } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="confirm_password"
                          className={`block text-sm font-medium mb-2 ${
                            isDarkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          id="confirm_password"
                          name="confirm_password"
                          value={formData.confirm_password}
                          onChange={handleChange}
                          className={`w-full px-4 py-2 rounded-lg border ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white focus:border-orange-500"
                              : "bg-white border-gray-300 text-gray-900 focus:border-orange-500"
                          } focus:outline-none focus:ring-2 focus:ring-orange-500/50`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-orange-600 hover:bg-orange-700 text-white"
                    }`}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar - Theme & Quick Info */}
          <div className="space-y-6">
            {/* Theme Settings */}
            <div
              className={`rounded-lg shadow-md p-6 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Appearance
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Dark Mode
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {isDarkMode ? "Enabled" : "Disabled"}
                    </p>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      isDarkMode ? "bg-orange-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isDarkMode ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {isDarkMode ? (
                      <svg
                        className="w-8 h-8 text-orange-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-8 h-8 text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    )}
                    <div>
                      <p
                        className={`text-sm font-medium ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {isDarkMode ? "Dark Theme" : "Light Theme"}
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Currently active
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Info */}
            <div
              className={`rounded-lg shadow-md p-6 ${
                isDarkMode ? "bg-gray-800" : "bg-white"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Account Status
              </h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Account Active
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Role: {user?.is_admin ? "Administrator" : "Inspector"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Device Token Settings */}
          <DeviceTokenSettings />
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
